<template>
  <!-- 내선 sub-tab — 회사 내선 전부(GET pbx-extensions). 목록·조작 데이터는 호스트가 준 inventory 하나로 받는다.
       계약: ~/md/front/studio-내선·전화기·사이트-탭-요청.md "화면 명세 › 내선"
       admin 이식: ~/md/front/admin-콜봇-내선관리-화면-요청.md "요청 계약 3"(상담사 번호 링크 제거·빈 상태 문구·역할 판정 제거) -->
  <section class="ext-panel" aria-label="내선">
    <!-- 4단계(GET pbx-extensions)도 없는 환경(stage·prod) — 안내 한 줄만 -->
    <p v-if="stage4 === false" class="ext-panel__unavailable">
      이 환경의 call-api 에는 아직 내선 발급(4단계)이 배포되지 않았습니다.
    </p>

    <template v-else>
      <header class="ext-panel__header">
        <h2 class="ext-panel__title">
          내선
          <span v-if="loaded" class="ext-panel__count">{{ items.length }}</span>
        </h2>
        <a-tooltip :title="refreshHint">
          <!-- 비활성 버튼은 마우스 이벤트를 먹어 툴팁이 안 뜬다 — span 으로 감싼다 -->
          <span class="ext-panel__btn-wrap">
            <a-button
              size="small"
              :disabled="stage7 !== true"
              :loading="isPending(REFRESH_KEY)"
              @click="onRefresh"
            >
              <template #icon><ReloadOutlined /></template>
              지금 확인
            </a-button>
          </span>
        </a-tooltip>
      </header>

      <!-- 목록은 있는데 재조회가 실패한 경우 — 목록은 두고 실패만 알린다 -->
      <p v-if="error && items.length" class="ext-panel__notice">
        내선 목록을 불러오지 못했습니다.
        <a href="#" @click.prevent="retryExtensions">다시 시도</a>
      </p>
      <p v-else-if="joinError" class="ext-panel__notice">
        상담사 번호·전화기 정보를 불러오지 못했습니다.
        <a href="#" @click.prevent="retryJoins">다시 시도</a>
      </p>
      <!-- 7단계 판정 실패 — 미배포가 아니라 확인을 못 한 것. 일부 작업이 막힌 이유와 다시 시도 -->
      <p v-if="stage7ProbeFailed" class="ext-panel__notice">
        내선 관리 기능을 확인하지 못했습니다.
        <a href="#" @click.prevent="retryProbe">다시 시도</a>
      </p>

      <div class="ext-panel__card">
        <p v-if="error && !items.length" class="ext-panel__state">
          내선 목록을 불러오지 못했습니다.
          <a href="#" @click.prevent="retryExtensions">다시 시도</a>
        </p>
        <div v-else-if="!loaded" class="ext-panel__skeleton">
          <a-skeleton active :paragraph="{ rows: 6 }" :title="false" />
        </div>
        <p v-else-if="!items.length" class="ext-panel__state">
          아직 발급된 내선이 없습니다. 병원이 studio 사용자 관리 › 상담사 번호에서 [내선번호 사용]을 켜면 여기에 나타납니다.
        </p>

        <div v-else class="ext-panel__table-wrap">
          <table class="ext-panel__table">
            <thead>
              <tr>
                <th scope="col">번호</th>
                <th scope="col">출처</th>
                <th scope="col">상태</th>
                <th scope="col">연결 상담사 번호</th>
                <th scope="col">PBX 반영</th>
                <th scope="col">전화기(등록)</th>
                <th scope="col">전화기(장치)</th>
                <th scope="col">회전</th>
                <th scope="col">마지막 확인</th>
                <th scope="col" class="ext-panel__actions-col">작업</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in rows" :key="row.ext.id">
                <td class="ext-panel__number">{{ row.number }}</td>

                <td>
                  <a-tooltip :title="EXTENSION_ORIGIN_HINT">
                    <span class="ext-panel__origin">{{ row.originLabel }}</span>
                  </a-tooltip>
                </td>

                <td><PbxBadge :badge="row.statusBadge" /></td>

                <td>
                  <!-- admin 에는 상담사 번호 화면이 없다 — 링크 없이 이름만 -->
                  <span v-if="row.counselor" :title="row.counselor.title">
                    {{ row.counselor.name }}
                  </span>
                  <span v-else class="ext-panel__dash">—</span>
                </td>

                <td>
                  <PbxBadge v-if="row.syncBadge" :badge="row.syncBadge" />
                </td>

                <td>
                  <!-- 줄이 없으면(4단계 응답·미관측) title 슬롯을 넘기지 않아 빈 툴팁이 뜨지 않는다 -->
                  <a-tooltip>
                    <template v-if="row.registrationLines.length" #title>
                      <div v-for="(line, i) in row.registrationLines" :key="i">{{ line }}</div>
                    </template>
                    <span><PbxBadge :badge="row.registration" /></span>
                  </a-tooltip>
                </td>

                <td>
                  <span v-if="row.device.kind === 'unknown'" class="ext-panel__dash">—</span>
                  <span v-else-if="row.device.kind === 'linked'" :title="row.device.title">
                    {{ row.device.label }}
                  </span>
                  <span v-else class="ext-panel__device-none">
                    없음
                    <button
                      v-if="row.device.canLink"
                      type="button"
                      class="ext-panel__link-btn"
                      @click="goDevices"
                    >
                      [전화기 연결]
                    </button>
                  </span>
                </td>

                <td :title="row.rotatedTitle">
                  <span v-if="row.rotation === undefined" class="ext-panel__dash">—</span>
                  <PbxBadge v-else-if="row.rotation" :badge="row.rotation" />
                </td>

                <td>
                  <span v-if="row.checked" :title="row.checked.relative">{{ row.checked.text }}</span>
                  <span v-else class="ext-panel__dash">—</span>
                </td>

                <td class="ext-panel__actions-col">
                  <div v-if="row.inline.length || row.menu.length" class="ext-panel__actions">
                    <a-tooltip
                      v-for="a in row.inline"
                      :key="a.key"
                      :title="a.enabled ? undefined : a.reason"
                    >
                      <span class="ext-panel__btn-wrap">
                        <a-button
                          size="small"
                          :danger="a.danger"
                          :disabled="!a.enabled || row.busy"
                          :loading="isPending(actionKey(a.key, row.ext))"
                          @click="onAction(a.key, row.ext)"
                        >
                          {{ a.label }}
                        </a-button>
                      </span>
                    </a-tooltip>

                    <a-dropdown v-if="row.menu.length" :trigger="['click']" :disabled="row.busy">
                      <a-button size="small" aria-label="더보기" :disabled="row.busy">
                        <template #icon><MoreOutlined /></template>
                      </a-button>
                      <template #overlay>
                        <a-menu @click="(info) => onAction(info.key, row.ext)">
                          <a-menu-item
                            v-for="a in row.menu"
                            :key="a.key"
                            :disabled="!a.enabled"
                            :danger="a.danger && a.enabled"
                          >
                            <div class="ext-panel__menu-label">{{ a.label }}</div>
                            <!-- 메뉴 안 비활성 항목은 툴팁 대신 이유를 바로 아래에 적는다 -->
                            <div v-if="!a.enabled && a.reason" class="ext-panel__menu-reason">
                              {{ a.reason }}
                            </div>
                          </a-menu-item>
                        </a-menu>
                      </template>
                    </a-dropdown>
                  </div>
                  <span v-else class="ext-panel__dash">—</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>

    <!-- 체크박스가 있는 확인창(재개·잠금 해제) — 체크 상태에 따라 문구가 바뀌어 Modal.confirm 대신 직접 그린다.
         구조는 admin 모달 규약(modal-title·modal-content·modal-footer·yellow-button) -->
    <a-modal
      :open="optModal.open"
      :footer="null"
      :width="440"
      :mask-closable="false"
      @cancel="closeOptModal"
    >
      <template #title>
        <div class="modal-title">{{ optModalCopy.title }}</div>
      </template>

      <div class="modal-content">
        <p class="ext-panel__confirm-text">{{ optModalCopy.text }}</p>
        <a-checkbox v-model:checked="optModal.checked">{{ optModalCopy.checkbox }}</a-checkbox>

        <div class="modal-footer">
          <a-button @click="closeOptModal">취소</a-button>
          <a-button type="primary" class="yellow-button" :loading="optModalPending" @click="submitOptModal">
            {{ optModalCopy.okText }}
          </a-button>
        </div>
      </div>
    </a-modal>
  </section>
</template>

<script setup>
import { MoreOutlined, ReloadOutlined } from '@ant-design/icons-vue';
import { message, Modal } from 'ant-design-vue';
import { computed, reactive } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import PbxBadge from './PbxBadge.vue';
import { usePbxAction } from '@/views/Callbot/service/usePbxAction';
import {
  checkSyncOutcome,
  EXTENSION_ORIGIN_HINT,
  extensionActionStates,
  extensionOriginLabel,
  extensionStatusBadge,
  formatPbxRelative,
  formatPbxTime,
  formatTelephoneNumber,
  phoneRegistrationBadge,
  registrationTooltipLines,
  rotationChip,
  STAGE7_PROBE_FAILED_HINT,
  STAGE7_UNAVAILABLE_HINT,
  syncStateBadge,
  compareExtensionNumber,
} from '@/views/Callbot/service/pbxExtensions';

/**
 * @typedef {import('@/views/Callbot/service/pbxExtensions').ExtensionActionKey} ExtensionActionKey
 * @typedef {import('@/views/Callbot/service/pbxExtensions').ExtensionActionState} ExtensionActionState
 * @typedef {import('@/views/Callbot/service/pbxExtensions').PbxBadge} PbxBadgeModel
 * @typedef {import('@/views/Callbot/api').PbxExtensionItem} PbxExtensionItem
 * @typedef {import('@/views/Callbot/api').PbxExtensionRotation} PbxExtensionRotation
 */

const props = defineProps({
  /** usePbxInventory(companyIdRef) 가 돌려준 객체 — 호스트(ExtensionManage)가 준다 */
  inventory: { type: Object, required: true },
});

const route = useRoute();
const router = useRouter();
const { run, isPending } = usePbxAction(props.inventory);

const REFRESH_KEY = 'extensions:refresh';
// 조작 뒤 재조회 — 행 상태·전화기 조인·상담사 번호 요약이 함께 바뀐다
const AFTER_ACTION_RELOAD = ['extensions', 'devices', 'counselorNumbers'];

// 조사(을/를·에)는 작업명마다 달라 문장을 통째로 둔다
/** @type {Record<ExtensionActionKey, { label: string, done: string, fail: string }>} */
const ACTION_COPY = {
  sync: { label: '재동기화', done: '재동기화를 완료했습니다.', fail: '재동기화에 실패했습니다.' },
  rotate: { label: '시크릿 회전', done: '시크릿 회전을 완료했습니다.', fail: '시크릿 회전에 실패했습니다.' },
  checkSync: { label: '설정 재수신', done: '', fail: '설정 재수신 요청에 실패했습니다.' },
  disable: { label: '일시 중지', done: '일시 중지를 완료했습니다.', fail: '일시 중지에 실패했습니다.' },
  resume: { label: '재개', done: '재개를 완료했습니다.', fail: '재개에 실패했습니다.' },
  lock: { label: '잠금', done: '잠금을 완료했습니다.', fail: '잠금에 실패했습니다.' },
  unlock: { label: '잠금 해제', done: '잠금 해제를 완료했습니다.', fail: '잠금 해제에 실패했습니다.' },
  release: { label: '번호 해제', done: '번호 해제를 완료했습니다.', fail: '번호 해제에 실패했습니다.' },
};

/** @type {ExtensionActionKey[]} */
const ACTION_KEYS = Object.keys(ACTION_COPY);

// ---------------------------------------------------------------------------
// 목록 상태
// ---------------------------------------------------------------------------

const stage4 = computed(() => props.inventory.stage4.value);
const stage7 = computed(() => props.inventory.stage7.value);
const stage7ProbeFailed = computed(() => props.inventory.stage7ProbeFailed.value);
// 서버는 최신순으로 준다 — 화면은 번호순이 찾기 쉽다
const items = computed(() => [...props.inventory.extensions.items.value].sort(compareExtensionNumber));
const loaded = computed(() => props.inventory.extensions.loaded.value);
const error = computed(() => props.inventory.extensions.error.value);
// 조인 대상 실패 — 이름·라벨 칸이 비어 보이는 이유를 알린다(전화기는 7단계에서만 부른다)
const joinError = computed(
  () =>
    props.inventory.counselorNumbers.error.value ||
    (stage7.value === true && props.inventory.devices.error.value)
);

const refreshHint = computed(() => {
  if (stage7.value === false) return STAGE7_UNAVAILABLE_HINT;
  return stage7ProbeFailed.value ? STAGE7_PROBE_FAILED_HINT : undefined;
});

function retryExtensions() {
  void props.inventory.reload(['extensions']);
}

function retryJoins() {
  void props.inventory.reload(['counselorNumbers', 'devices']);
}

function retryProbe() {
  void props.inventory.reprobe();
}

// ---------------------------------------------------------------------------
// 행 모델 — 라벨·파생 판정은 전부 pbxExtensions.js 순수 함수, 여기선 조인과 조립만
// ---------------------------------------------------------------------------

/**
 * @typedef {ExtensionActionState & { key: ExtensionActionKey, label: string, danger?: boolean }} RowAction
 * @typedef {{ kind: 'unknown' } | { kind: 'none', canLink: boolean } | { kind: 'linked', label: string, title?: string }} DeviceCell
 * @typedef {Object} ExtensionRow
 * @property {PbxExtensionItem} ext
 * @property {string} number
 * @property {string} originLabel
 * @property {PbxBadgeModel} statusBadge
 * @property {{ name: string, title?: string } | null} counselor
 * @property {PbxBadgeModel | null} syncBadge
 * @property {PbxBadgeModel} registration
 * @property {string[]} registrationLines
 * @property {DeviceCell} device
 * @property {PbxBadgeModel | null | undefined} rotation undefined = 4단계 응답(필드 없음, `—`), null = 회전 없음(빈 칸)
 * @property {string=} rotatedTitle
 * @property {{ text: string, relative: string } | null} checked
 * @property {RowAction[]} inline
 * @property {RowAction[]} menu
 * @property {boolean} busy
 */

/**
 * @param {ExtensionActionKey} action
 * @param {{ id: string }} ext
 * @returns {string}
 */
function actionKey(action, ext) {
  return `extension:${action}:${ext.id}`;
}

/**
 * 행 상태에 맞는 작업만 보인다. 자주 쓰는 것은 버튼, 드문 것(회전·일시 중지·잠금)은 더보기 메뉴.
 * 보이는 작업의 활성 여부·비활성 이유는 extensionActionStates 가 정한다(연결 안 된 내선 등).
 * @param {PbxExtensionItem} ext
 * @param {Record<ExtensionActionKey, ExtensionActionState>} states
 * @returns {{ inline: RowAction[], menu: RowAction[] }}
 */
function rowActions(ext, states) {
  const make = (key, danger = false) => ({
    key,
    label: ACTION_COPY[key].label,
    danger,
    ...states[key],
  });
  switch (ext.status) {
    case 'active':
      return {
        inline: [make('sync'), make('checkSync')],
        menu: [make('rotate'), make('disable'), make('lock', true)],
      };
    case 'disabled':
      return { inline: [make('resume')], menu: [make('lock', true)] };
    case 'locked':
      return { inline: [make('unlock')], menu: [] };
    case 'retired':
    case 'blocked':
      // admin 은 CERTILIFE 뿐이고 강제는 call-api 가 한다 — 역할 판정 없이 회수·차단 상태면 항상 보인다
      return { inline: [make('release', true)], menu: [] };
    default:
      // legacy — PBX 에 정적으로 정의된 회선이라 call-api 조작 대상이 아니다
      return { inline: [], menu: [] };
  }
}

/**
 * @param {PbxExtensionItem} ext
 * @returns {ExtensionRow['counselor']}
 */
function counselorCell(ext) {
  const id = ext.counselor_number_id;
  if (!id) return null;
  const cn = props.inventory.counselorNumberById.value.get(id);
  if (!cn) return { name: '연결됨', title: `상담사 번호 ${id}` };
  return { name: cn.name, title: formatTelephoneNumber(cn.number) };
}

/**
 * @param {PbxExtensionItem} ext
 * @returns {DeviceCell}
 */
function deviceCell(ext) {
  if (ext.device_id === undefined) return { kind: 'unknown' };
  if (ext.device_id === null) {
    // 전화기 연결(claim)은 사용 중인 상담사 번호 내선만 대상이다 — 그 밖엔 링크를 걸지 않는다
    const canLink = stage7.value === true && ext.status === 'active' && !!ext.counselor_number_id;
    return { kind: 'none', canLink };
  }
  const device = props.inventory.deviceById.value.get(ext.device_id);
  if (!device) return { kind: 'linked', label: '연결됨', title: `전화기 ${ext.device_id}` };
  return {
    kind: 'linked',
    label: device.label ?? device.mac,
    title: device.label ? device.mac : undefined,
  };
}

/**
 * @param {PbxExtensionItem} ext
 * @returns {PbxBadgeModel}
 */
function statusBadgeOf(ext) {
  const badge = extensionStatusBadge(ext.status);
  if (ext.status === 'disabled' && ext.disabled_at != null) {
    return { ...badge, title: `일시 중지 ${formatPbxTime(ext.disabled_at)}` };
  }
  return badge;
}

/**
 * @param {PbxExtensionItem} ext
 * @returns {PbxBadgeModel | null}
 */
function syncBadgeOf(ext) {
  const badge = syncStateBadge(ext.sync_state);
  if (!badge) return null;
  return { ...badge, title: ext.last_error_code ? `오류 코드 ${ext.last_error_code}` : undefined };
}

/** @type {import('vue').ComputedRef<ExtensionRow[]>} */
const rows = computed(() => {
  const now = props.inventory.now.value;
  const opts = {
    stage7: stage7.value,
    canRelease: props.inventory.canRelease.value,
    stage7ProbeFailed: stage7ProbeFailed.value,
  };
  const deviceById = props.inventory.deviceById.value;
  return items.value.map((ext) => {
    const device = ext.device_id ? deviceById.get(ext.device_id) : undefined;
    const reg = phoneRegistrationBadge(ext, device, now);
    // 파생 칩의 설명 + 등록 상세 줄을 한 툴팁에 모은다(배지 자체 title 과 겹치지 않게 뺀다)
    const regLines = [reg.title, ...registrationTooltipLines(ext)].filter((l) => !!l);
    const { inline, menu } = rowActions(ext, extensionActionStates(ext, opts));
    const checkedAt = ext.checked_at;
    return {
      ext,
      number: formatTelephoneNumber(ext.number),
      originLabel: extensionOriginLabel(ext.origin),
      statusBadge: statusBadgeOf(ext),
      counselor: counselorCell(ext),
      syncBadge: syncBadgeOf(ext),
      registration: { label: reg.label, tone: reg.tone },
      registrationLines: regLines,
      device: deviceCell(ext),
      rotation: ext.rotation === undefined ? undefined : rotationChip(ext.rotation),
      rotatedTitle:
        ext.secret_rotated_at != null ? `마지막 시크릿 회전 ${formatPbxTime(ext.secret_rotated_at)}` : undefined,
      checked:
        checkedAt != null
          ? { text: formatPbxTime(checkedAt), relative: formatPbxRelative(checkedAt, now) }
          : null,
      inline,
      menu,
      busy: ACTION_KEYS.some((k) => isPending(actionKey(k, ext))),
    };
  });
});

function goDevices() {
  // 다른 query(companyId·pbxMock 등)는 보존한다 — 호스트 sub 전환과 같은 방식
  void router.replace({ query: { ...route.query, sub: 'devices' } });
}

// ---------------------------------------------------------------------------
// 지금 확인 — 호스트 스냅샷을 새로 받아 목록을 통째로 교체
// ---------------------------------------------------------------------------

async function onRefresh() {
  // 응답을 기다리는 사이 조작 뒤 재조회가 시작되면 그 결과를 버리지 않게 순번을 떠 둔다
  const since = props.inventory.extensionsSeq();
  // 503 PBX_EXTENSION_HOST_UNAVAILABLE 이면 run 이 서버 문구를 토스트로 보이고 목록은 그대로 둔다
  const res = await run(REFRESH_KEY, () => props.inventory.api.refreshPbxExtensions(), {
    success: '상태 확인을 완료했습니다.',
    fallback: '내선 상태를 확인하지 못했습니다.',
  });
  if (res) props.inventory.replaceExtensions(res.items ?? [], since);
}

// ---------------------------------------------------------------------------
// 행 작업
// ---------------------------------------------------------------------------

/**
 * 조작 응답(항목 전체)을 재조회 전에 행에 먼저 얹는다 — 회전을 일으킨 조작은 응답 직후
 * `반영 중` 칩이 바로 보여야 한다. run 이 곧이어 재조회하므로 이 값은 서버 목록으로 덮인다.
 * replaceExtensions 를 쓰지 않는 이유: 순번을 올려 방금 시작한 재조회 결과를 버리게 된다.
 * @param {PbxExtensionItem} ext
 * @param {PbxExtensionItem & { warnings?: Array }} res
 * @param {boolean} rotating
 */
function applyActionResponse(ext, res, rotating) {
  if (!res || typeof res !== 'object' || res.id !== ext.id) return;
  // warnings 는 토스트용이라 행에 얹지 않는다(admin ESLint 는 구조 분해로 버린 변수도 잡는다 — delete 로 뺀다)
  /** @type {PbxExtensionItem} */
  const patched = { ...res };
  delete patched.warnings;
  // 서버가 rotation 을 보냈으면(null·종결 상태 포함) 그대로 둔다 — 로컬 시각 started_at 의
  // 가짜 pending 은 서버 값을 덮고, 뒤따르는 폴링 재조회 결과보다 오래된 것으로 보이게 만든다
  if (rotating && patched.rotation === undefined) {
    /** @type {PbxExtensionRotation} */
    const pending = {
      state: 'pending',
      reason: 'manual',
      started_at: Date.now(),
      promoted_at: null,
      notify: { at: null, outcome: null },
    };
    patched.rotation = pending;
  }
  const state = props.inventory.extensions.items;
  state.value = state.value.map((x) => (x.id === ext.id ? { ...x, ...patched } : x));
}

/**
 * 상담사 번호 id 로 부르는 조작 공통 — 성공 토스트·재조회는 usePbxAction
 * @param {Exclude<ExtensionActionKey, 'release' | 'checkSync'>} action
 * @param {PbxExtensionItem} ext
 * @param {(counselorNumberId: string) => Promise<any>} call
 * @param {boolean=} rotating
 * @returns {Promise<unknown>}
 */
function runLinked(action, ext, call, rotating = false) {
  const cnId = ext.counselor_number_id;
  if (!cnId) return Promise.resolve();
  return run(
    actionKey(action, ext),
    async () => {
      const res = await call(cnId);
      applyActionResponse(ext, res, rotating);
      return res;
    },
    { success: ACTION_COPY[action].done, reload: AFTER_ACTION_RELOAD, fallback: ACTION_COPY[action].fail }
  );
}

/**
 * @param {{ title: string, content: string, okText: string, danger?: boolean, onOk: () => Promise<unknown> }} opts
 */
function confirm(opts) {
  Modal.confirm({
    title: opts.title,
    content: opts.content,
    okText: opts.okText,
    okType: opts.danger ? 'danger' : 'primary',
    cancelText: '취소',
    // 되돌리기 어려운 조작은 Enter 한 번에 실행되지 않게 취소에 초점을 둔다
    autoFocusButton: opts.danger ? 'cancel' : 'ok',
    // run 은 reject 하지 않는다 — 응답까지 확인 버튼이 로딩으로 남고, 실패는 토스트로 알린다
    onOk: () => opts.onOk(),
  });
}

/**
 * @param {PbxExtensionItem} ext
 * @param {boolean} force
 */
async function doCheckSync(ext, force) {
  const cnId = ext.counselor_number_id;
  if (!cnId) return;
  const res = await run(
    actionKey('checkSync', ext),
    () => props.inventory.api.checkSyncCounselorExtension(cnId, force ? { force: true } : {}),
    // 응답이 항상 200 이라 성공 토스트 대신 reason 별 안내를 아래에서 보인다
    { success: null, fallback: ACTION_COPY.checkSync.fail }
  );
  if (!res) return;
  const outcome = checkSyncOutcome(res);
  if (outcome.kind === 'info') {
    message.info(outcome.text);
    return;
  }
  // 강제 전송 뒤에도 통화 중이라고 오면 다시 묻지 않는다(무한 확인 방지)
  if (force) return;
  confirm({
    title: ACTION_COPY.checkSync.label,
    content: outcome.text,
    okText: '강제 전송',
    danger: true,
    onOk: () => doCheckSync(ext, true),
  });
}

/**
 * @param {ExtensionActionKey} key
 * @param {PbxExtensionItem} ext
 */
function onAction(key, ext) {
  const api = props.inventory.api;
  const n = ext.number;
  switch (key) {
    case 'sync':
      void runLinked('sync', ext, (id) => api.syncCounselorExtension(id));
      return;
    case 'checkSync':
      void doCheckSync(ext, false);
      return;
    case 'rotate':
      confirm({
        title: '시크릿 회전',
        content: `내선 ${n} 의 SIP 비밀번호를 새로 만들어 PBX 에 반영합니다. 전화기가 새 설정을 받을 때까지 이 내선은 잠시(수 초에서 설정 수신 주기까지) 등록이 끊길 수 있습니다. 통화 중이면 통화가 끝난 뒤 진행하세요. 계속할까요?`,
        okText: '회전',
        onOk: () => runLinked('rotate', ext, (id) => api.rotateCounselorExtensionSecret(id), true),
      });
      return;
    case 'disable':
      confirm({
        title: '일시 중지',
        content: `내선 ${n} 을 일시 중지합니다. PBX 에서 회선이 즉시 내려가 이 전화기로 받거나 걸 수 없고, 상담사 전환·발신 대상에서도 빠집니다. 전화기 화면의 회선은 다음 설정 수신 때 사라집니다. [재개]로 되돌릴 수 있습니다. 계속할까요?`,
        okText: '일시 중지',
        onOk: () => runLinked('disable', ext, (id) => api.disableCounselorExtension(id)),
      });
      return;
    case 'lock':
      confirm({
        title: '잠금',
        content: `내선 ${n} 을 잠급니다. 착·발신이 즉시 막히고 전화기 등록이 끊깁니다. 되돌리려면 [잠금 해제]를 눌러야 하며, 해제 시 기본으로 SIP 사용자명과 비밀번호가 재발급됩니다. 계속할까요?`,
        okText: '잠금',
        danger: true,
        onOk: () => runLinked('lock', ext, (id) => api.lockCounselorExtension(id)),
      });
      return;
    case 'resume':
    case 'unlock':
      openOptModal(key, ext);
      return;
    case 'release':
      confirm({
        title: '번호 해제',
        content: `내선 번호 ${n} 을 이 회사 보유에서 해제해 공용 번호 풀로 돌려보냅니다. 다른 회사에 다시 발급될 수 있습니다. 되돌릴 수 없습니다. 계속할까요?`,
        okText: '해제',
        danger: true,
        // 응답은 항목 전체가 아니다 — 행을 갈아끼우지 않고 재조회(해제된 행은 목록에서 빠진다)
        onOk: () =>
          run(actionKey('release', ext), () => api.releasePbxExtension(ext.id), {
            success: ACTION_COPY.release.done,
            reload: ['extensions'],
            fallback: ACTION_COPY.release.fail,
          }),
      });
      return;
  }
}

// ---------------------------------------------------------------------------
// 체크박스 확인창 — 재개(시크릿도 회전) · 잠금 해제(자격증명 유지)
// ---------------------------------------------------------------------------

/**
 * @type {{ open: boolean, kind: 'resume' | 'unlock', ext: PbxExtensionItem | null, checked: boolean }}
 * ext: 열린 동안 폴링·재조회로 행이 바뀌어도 누른 시점의 내선으로 조작한다
 */
const optModal = reactive({ open: false, kind: 'resume', ext: null, checked: false });

/**
 * @param {'resume' | 'unlock'} kind
 * @param {PbxExtensionItem} ext
 */
function openOptModal(kind, ext) {
  optModal.kind = kind;
  optModal.ext = ext;
  // 두 체크박스 모두 기본 해제(결정 8 — 잠금 해제는 기본 자격증명 재발급)
  optModal.checked = false;
  optModal.open = true;
}

function closeOptModal() {
  // 진행 중에도 닫을 수 있다(원본 confirm-loading 과 같다) — 중복 제출은 run 의 pending 키 가드가 막는다
  optModal.open = false;
}

const optModalCopy = computed(() => {
  const n = optModal.ext?.number ?? '';
  if (optModal.kind === 'resume') {
    const last = optModal.checked
      ? '시크릿을 함께 회전하므로 전화기는 새 설정을 받은 뒤(다음 설정 수신 또는 설정 재수신 성공 후) 다시 붙습니다.'
      : '전화기는 다음 설정 수신 때 회선을 다시 받습니다(중지 중 설정을 받지 않았다면 최대 2분 안에 스스로 붙습니다).';
    return {
      title: '재개',
      text: `내선 ${n} 을 재개합니다. ${last}`,
      checkbox: '시크릿도 회전',
      okText: '재개',
    };
  }
  return {
    title: '잠금 해제',
    text: `내선 ${n} 의 잠금을 해제합니다. 기본으로 SIP 사용자명과 비밀번호를 새로 발급해 PBX 에 반영합니다. 잠금 뒤 전화기 등록이 이미 끊겼으므로 새 설정은 전화기의 다음 설정 수신 때(또는 복구 코드로) 들어갑니다.`,
    checkbox: '자격증명 유지(실수로 잠근 경우)',
    okText: '해제',
  };
});

const optModalPending = computed(() => !!optModal.ext && isPending(actionKey(optModal.kind, optModal.ext)));

async function submitOptModal() {
  const ext = optModal.ext;
  if (!ext) return;
  const api = props.inventory.api;
  const checked = optModal.checked;
  if (optModal.kind === 'resume') {
    await runLinked('resume', ext, (id) => api.resumeCounselorExtension(id, { rotate_secret: checked }), checked);
  } else {
    // 자격증명을 유지하지 않으면(기본) 재발급 = 회전이 시작된다
    await runLinked(
      'unlock',
      ext,
      (id) => api.unlockCounselorExtension(id, { keep_credentials: checked }),
      !checked
    );
  }
  // Modal.confirm 과 같게 — 실패는 토스트로 알리고 창은 닫는다
  optModal.open = false;
}
</script>

<style scoped lang="scss">
.ext-panel {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  font-size: 13px;

  &__unavailable {
    margin: 0;
    padding: 24px;
    border: 1px solid #f0f0f0;
    border-radius: 12px;
    background: #fff;
    color: #8c8c8c;
    font-size: 13px;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex: none;
  }

  &__title {
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 0;
    font-size: 15px;
    font-weight: 700;
    color: #262626;
  }

  &__count {
    padding: 0 8px;
    border-radius: 999px;
    background: #f0f5ff;
    color: #1668dc;
    font-size: 12px;
    font-weight: 600;
    line-height: 20px;
  }

  &__btn-wrap {
    display: inline-block;
  }

  &__notice {
    flex: none;
    margin: 0;
    padding: 8px 12px;
    border: 1px solid #ffe58f;
    border-radius: 8px;
    background: #fffbe6;
    color: #8c6d1f;
    font-size: 12px;

    a {
      margin-left: 4px;
      color: #1668dc;
    }
  }

  &__card {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    border: 1px solid #f0f0f0;
    border-radius: 12px;
    background: #fff;
    overflow: hidden;
  }

  &__skeleton {
    padding: 16px 24px;
  }

  &__state {
    margin: 0;
    padding: 40px 24px;
    color: #8c8c8c;
    font-size: 13px;
    text-align: center;

    a {
      margin-left: 4px;
      color: #1668dc;
    }
  }

  // 좁은 폭에서는 페이지가 아니라 카드 안에서 가로 스크롤
  &__table-wrap {
    flex: 1;
    min-height: 0;
    overflow: auto;
  }

  &__table {
    width: 100%;
    min-width: 1240px;
    border-collapse: separate;
    border-spacing: 0;
    font-size: 13px;

    th {
      position: sticky;
      top: 0;
      z-index: 1;
      padding: 10px 14px;
      background: #fafafa;
      border-bottom: 1px solid #f0f0f0;
      color: #8c8c8c;
      font-size: 12px;
      font-weight: 600;
      text-align: left;
      white-space: nowrap;
    }

    td {
      padding: 10px 14px;
      border-bottom: 1px solid #f5f5f5;
      background: #fff;
      color: #262626;
      white-space: nowrap;
      vertical-align: middle;
    }

    tbody tr:last-child td {
      border-bottom: 0;
    }

    tbody tr:hover td {
      background: #fcfcfd;
    }
  }

  // 작업 열은 가로 스크롤 중에도 오른쪽에 붙여 둔다
  &__actions-col {
    position: sticky;
    right: 0;
    box-shadow: -6px 0 6px -6px rgba(0, 0, 0, 0.08);
  }

  &__table th.ext-panel__actions-col {
    z-index: 2;
  }

  &__number {
    font-weight: 600;
  }

  &__origin {
    color: #595959;
    cursor: help;
  }

  &__dash {
    color: #bfbfbf;
  }

  &__device-none {
    color: #8c8c8c;
  }

  &__link-btn {
    margin-left: 4px;
    padding: 0;
    border: 0;
    background: none;
    color: #1668dc;
    font-size: 13px;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  &__menu-label {
    font-size: 13px;
  }

  &__menu-reason {
    max-width: 260px;
    margin-top: 2px;
    color: #8c8c8c;
    font-size: 11px;
    white-space: normal;
  }

  &__confirm-text {
    margin: 0 0 12px;
    color: #262626;
    font-size: 13px;
    line-height: 1.6;
  }
}

// admin 모달 규약 — 전역 .modal-content(common.scss) 의 테두리·최소 높이는 antd 모달 안에서 쓰지 않는다
.modal-content {
  border: none;
  border-radius: 0;
  min-height: 0;
  display: block;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
}

/* 전역 정의가 없는 클래스라 쓰는 컴포넌트마다 들고 있어야 한다. */
:deep(.yellow-button) {
  background-color: #ffcc32 !important;
  border-color: #ffcc32 !important;
  color: #000 !important;

  &:hover {
    background-color: #e6b82e !important;
    border-color: #e6b82e !important;
  }

  &:disabled {
    background-color: #d9d9d9 !important;
    border-color: #d9d9d9 !important;
    color: rgba(0, 0, 0, 0.25) !important;
  }
}
</style>
